import { yup } from '@framework/validation';
import { ExpressionType } from '../../IExpression';
import { createExpressionType } from '../../IExpressionType';
import { isObjectsArrayField } from '../../datatype/Datatype';
import { EvaluationScope } from '../../evaluate/EvaluationScope';
import { IReferenceObjectValue } from '../../references/IReference';
import {
  expressionLogger,
  expressionLogger as logger,
} from '../../utils/expressionLogger';
import { ConditionExpressionType } from '../condition/ConditionExpression.type';
import { IConditionExpression } from '../condition/IConditionExpression';
import { ISubconditionsGroupExpression } from './ISubconditionsGroupExpression';

export const SubconditionGroupExpressionSchema: yup.ObjectSchema<ISubconditionsGroupExpression> =
  yup
    .object({
      type: yup
        .string()
        .exact(ExpressionType.SubconditionsGroup)
        .required()
        .default(ExpressionType.SubconditionsGroup),
      conditions: yup
        .array<any, IConditionExpression>(
          yup.lazy(() => ConditionExpressionType.schema),
        )
        .label('Condizioni elemento')
        .required(),
    })
    .noUnknown();

export const SubconditionGroupExpressionType =
  createExpressionType<ISubconditionsGroupExpression>({
    type: ExpressionType.SubconditionsGroup,
    schema: SubconditionGroupExpressionSchema,
    /**
     * Ritorna il numero di elementi del parent che soddisfano **tutte**
     * le condizioni specificate.
     */
    evaluate(expression, scope) {
      const parent = scope.getParent();
      if (!isObjectsArrayField(parent?.reference.field)) {
        logger.warn(`Parent is not an object array field`, { parent, expression }); // prettier-ignore
      }

      expressionLogger.debug(`[SubconditionGroupExpression.evaluate] Valutazione subcondizioni`, { expression, parent }); // prettier-ignore

      if (!parent || !Array.isArray(parent?.value)) {
        return null;
      }

      return (parent.value as IReferenceObjectValue[]).filter(item => {
        return evaluateSubconditionsOnItem(scope, expression, item);
      });
    },
    /**
     * Le sottocondizioni possono avere reference ma non consideriamo quelle
     * nel membro a Sx perché fa riferimento a un campo della lista padre
     */
    getReferences(expression, collect) {
      return expression.conditions.flatMap(cond => {
        return cond.arguments.flatMap(collect);
      });
    },
    toString(expression, toString) {
      return '( ' + expression.conditions.map(toString).join(' && ') + ' )';
    },
  });

/**
 * Valuta tutte le condizioni specificate su un elemento.
 * Ritorna `true` se tutte le condizioni sono soddisfatte, `false` altrimenti.
 */
function evaluateSubconditionsOnItem(
  scope: EvaluationScope,
  expression: ISubconditionsGroupExpression,
  item: IReferenceObjectValue,
) {
  // Aggiungiamo le variabili dell'item allo scope corrente, creando uno scope
  // dedicato.
  const itemScope = scope.addChildVariablesFromParent(item);

  for (const condition of expression.conditions) {
    const conditionValue = itemScope.evaluate(condition);
    if (!conditionValue) {
      return false;
    }
  }
  return true;
}
